/**
 * Everything the game plays, through one AudioContext.
 *
 * What a moment sounds like is content: the library is a list of files by name
 * and each cue a list of names, and both arrive from the world and change
 * whenever the tools change them. This only decides how loud, where from, and
 * whether it is playing at all.
 *
 * A browser will not make a sound before somebody has pressed something, so
 * nothing is set up until the first key or click. Sounds asked for before then
 * are simply not heard.
 *
 * Files are fetched when they are first wanted rather than at login. The whole
 * library is megabytes, most of it background loops nobody hears more than one
 * of, and pulling it down before the world has drawn costs the player the one
 * moment that matters.
 */

import { SOUND_CUES, type SoundGroup } from '@inverse/common';
import type { SoundCueWire, SoundLibraryWire, SoundWire } from '@inverse/protocol';

export type SoundChannel = 'effects' | 'interface' | 'ambient';

export interface SoundVolumes {
  /** Everything, nought to one. */
  master: number;
  effects: number;
  interface: number;
  ambient: number;
  muted: boolean;
}

/** Where a sound comes from, in world tiles. Nothing is right beside the listener. */
export interface SoundPlace {
  x: number;
  z: number;
}

/** Past this, in tiles, nothing is heard. The world stops sending at sixteen. */
const HEARD_WITHIN = 18;

/** How long a sound that has not arrived yet may keep somebody waiting before it is dropped. */
const LATE_BY_MS = 250;

/** How quickly the background moves from one mood to the next, in seconds. */
const AMBIENT_EASE = 2.5;

const GROUP_OF = new Map<string, SoundGroup>(SOUND_CUES.map((one) => [one.cue, one.group]));

function channelOf(cue: string): SoundChannel {
  const group = GROUP_OF.get(cue);
  return group === 'interface' ? 'interface' : group === 'ambient' ? 'ambient' : 'effects';
}

/**
 * Where the files are asked for, relative to the page so it works behind
 * Discord's prefix too.
 *
 * Not under a name that ends in `.wav`, and not answered as audio. A download
 * manager or a media grabbing extension watches for audio going past and takes
 * a copy of it, so a player with one installed had every sound the game played
 * saved into their downloads folder. Nothing the page does can stop that once
 * the response looks like a media file, so it does not: the dots in the name
 * are carried as tildes and the bytes come back as bytes. The tools still ask
 * for them under `sounds/uploaded/`, where a sound is meant to be a sound.
 */
function urlOf(file: string): string {
  return `data/s/${encodeURIComponent(file.replaceAll('.', '~'))}`;
}

interface Loop {
  source: AudioBufferSourceNode;
  gain: GainNode;
  sound: string;
}

export class SoundEngine {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private channels: Partial<Record<SoundChannel, GainNode>> = {};

  private sounds = new Map<string, SoundWire>();
  private cues = new Map<string, SoundCueWire>();
  private npcs = new Map<number, { attack: string; hurt: string; die: string }>();

  /** Decoded sounds by file, and the ones on their way. */
  private buffers = new Map<string, AudioBuffer>();
  private loading = new Map<string, Promise<AudioBuffer | null>>();

  private volumes: SoundVolumes = {
    master: 0.7,
    effects: 1,
    interface: 0.6,
    ambient: 0.5,
    muted: false,
  };
  private listener = { x: 0, z: 0, yaw: 0 };

  /** The background loops playing now, by cue, and how loud each is meant to be. */
  private loops = new Map<string, Loop>();
  private ambientWanted = new Map<string, number>();

  constructor() {
    if (typeof window === 'undefined') return;
    const wake = (): void => {
      this.unlock();
      if (this.context?.state === 'running') {
        window.removeEventListener('pointerdown', wake, true);
        window.removeEventListener('keydown', wake, true);
      }
    };
    window.addEventListener('pointerdown', wake, true);
    window.addEventListener('keydown', wake, true);

    // Every button makes the same small sound, unless what it did has a sound
    // of its own: a panel opening is heard as that rather than as both.
    window.addEventListener(
      'click',
      (event) => {
        const target = event.target instanceof Element ? event.target.closest('button') : null;
        if (!target || target.disabled) return;
        const asked = this.interfaceAt;
        window.setTimeout(() => {
          if (this.interfaceAt === asked) this.cue('click');
        }, 0);
      },
      true,
    );
  }

  /** When an interface sound other than a click last played, so a click does not double it. */
  private interfaceAt = 0;

  /** The first press: the context can be made now, and the buttons made ready. */
  unlock(): void {
    if (!this.context) {
      const Context =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Context) return;
      this.context = new Context();
      this.master = this.context.createGain();
      this.master.connect(this.context.destination);
      for (const channel of ['effects', 'interface', 'ambient'] as const) {
        const gain = this.context.createGain();
        gain.connect(this.master);
        this.channels[channel] = gain;
      }
      this.applyVolumes();
      this.warmInterface();
    }
    if (this.context.state === 'suspended') void this.context.resume();
  }

  /** The library as the world sent it. Files no longer used are let go. */
  setLibrary(json: string): void {
    let parsed: SoundLibraryWire;
    try {
      parsed = JSON.parse(json) as SoundLibraryWire;
    } catch {
      return;
    }
    this.sounds = new Map(parsed.sounds.map((one) => [one.name, one]));
    this.cues = new Map(parsed.cues.map((one) => [one.cue, one]));
    this.npcs = new Map((parsed.npcs ?? []).map((one) => [one.id, one]));
    this.snapshot = { sounds: parsed.sounds, cues: parsed.cues, npcs: parsed.npcs ?? [] };
    for (const watcher of this.watchers) watcher();

    const files = new Set(parsed.sounds.map((one) => one.file));
    for (const file of [...this.buffers.keys()]) if (!files.has(file)) this.buffers.delete(file);
    this.queue = this.queue.filter((file) => files.has(file));

    // A loop whose sound was swapped starts again with the new one.
    for (const [cue, loop] of this.loops) {
      if (!this.cues.get(cue)?.sounds.includes(loop.sound)) this.stopLoop(cue);
    }
    this.warmInterface();
  }

  /** The library as last sent, the same object until it changes, for the tools to read. */
  private snapshot: SoundLibraryWire = { sounds: [], cues: [], npcs: [] };
  private watchers = new Set<() => void>();

  library = (): SoundLibraryWire => this.snapshot;

  /** Told whenever the library changes. Returns how to stop being told. */
  watch = (watcher: () => void): (() => void) => {
    this.watchers.add(watcher);
    return () => this.watchers.delete(watcher);
  };

  setVolumes(volumes: SoundVolumes): void {
    this.volumes = volumes;
    this.applyVolumes();
    // Turning the background up is what fetches it, if it was never wanted.
    this.applyAmbient();
  }

  /** Where the listener is and which way the camera faces, so sounds come from the right side. */
  setListener(x: number, z: number, yaw: number): void {
    this.listener = { x, z, yaw };
  }

  /** One of the game's moments, from a place or from right beside the listener. */
  cue(cue: string, at: SoundPlace | null = null, loudness = 1): void {
    if (cue !== 'click' && channelOf(cue) === 'interface') this.interfaceAt = performance.now();
    const held = this.cues.get(cue);
    if (!held || held.sounds.length === 0) return;
    const name = held.sounds[Math.floor(Math.random() * held.sounds.length)] ?? '';
    const sound = this.sounds.get(name);
    if (sound) this.play(sound, channelOf(cue), at, loudness * held.volume);

    // A moment that has happened once will happen again, and the next time it
    // may pick a different one of its sounds: the rest follow quietly behind.
    if (held.sounds.length > 1) this.warm(held.sounds);
  }

  /** One sound by its name in the library. */
  sound(name: string, at: SoundPlace | null = null, loudness = 1): void {
    const sound = this.sounds.get(name);
    if (sound) this.play(sound, 'effects', at, loudness);
  }

  /** A character's own sound for something, or the one everybody makes. */
  npcSound(
    npcId: number,
    kind: 'attack' | 'hurt' | 'die',
    fallback: string,
    at: SoundPlace | null,
  ): void {
    const own = this.npcs.get(npcId)?.[kind];
    if (own) this.sound(own, at);
    else this.cue(fallback, at);
  }

  /** For the tools: a sound heard as it is, at full loudness, wherever the camera is. */
  preview(name: string): void {
    this.unlock();
    this.interfaceAt = performance.now();
    const sound = this.sounds.get(name);
    if (sound) this.play(sound, 'interface', null, 1, true);
  }

  /** For the tools: a cue as the game would play it. */
  previewCue(cue: string): void {
    this.unlock();
    this.interfaceAt = performance.now();
    const held = this.cues.get(cue);
    const name = held?.sounds[Math.floor(Math.random() * (held?.sounds.length ?? 0))];
    const sound = name ? this.sounds.get(name) : undefined;
    if (sound) this.play(sound, 'interface', null, held?.volume ?? 1, true);
  }

  /**
   * How much of each background there should be, nought to one each.
   *
   * Loops that are wanted are started and eased up; ones that are not are
   * eased down and stopped once they are silent.
   */
  setAmbient(wanted: Readonly<Record<string, number>>): void {
    this.ambientWanted = new Map(Object.entries(wanted));
    this.applyAmbient();
  }

  private applyAmbient(): void {
    const context = this.context;
    if (!context) return;

    // A background loop is the largest file in the library, so one is never
    // fetched while there is nothing to hear it through.
    const heard = !this.volumes.muted && this.volumes.master > 0 && this.volumes.ambient > 0;

    for (const [cue, level] of this.ambientWanted) {
      if (level <= 0.001 || !heard) continue;
      if (!this.loops.has(cue)) this.startLoop(cue);
    }
    for (const [cue, loop] of this.loops) {
      const level = this.ambientWanted.get(cue) ?? 0;
      const sound = this.sounds.get(loop.sound);
      const target = level * (sound?.volume ?? 1) * (this.cues.get(cue)?.volume ?? 1);
      loop.gain.gain.setTargetAtTime(target, context.currentTime, AMBIENT_EASE / 3);
      if (level <= 0.001 && loop.gain.gain.value < 0.005) this.stopLoop(cue);
    }
  }

  // -------------------------------------------------------------------------

  private applyVolumes(): void {
    const context = this.context;
    if (!context || !this.master) return;
    const now = context.currentTime;
    this.master.gain.setTargetAtTime(this.volumes.muted ? 0 : this.volumes.master, now, 0.05);
    this.channels.effects?.gain.setTargetAtTime(this.volumes.effects, now, 0.05);
    this.channels.interface?.gain.setTargetAtTime(this.volumes.interface, now, 0.05);
    this.channels.ambient?.gain.setTargetAtTime(this.volumes.ambient, now, 0.05);
  }

  /**
   * The buttons and panels, fetched one after another as soon as there is a
   * context to decode them with.
   *
   * These are the only sounds fetched before they are wanted: they are small,
   * and a button that is silent the first time it is pressed is the one thing
   * here somebody would notice. Everything else arrives on its first playing.
   */
  private warmInterface(): void {
    for (const cue of SOUND_CUES) {
      if (cue.group === 'interface') this.warm(this.cues.get(cue.cue)?.sounds ?? []);
    }
  }

  /**
   * Fetches the files behind these sounds quietly, one at a time, so nothing
   * competes with the world for the connection.
   */
  private warm(names: readonly string[]): void {
    if (!this.context) return;
    for (const name of names) {
      const file = this.sounds.get(name)?.file;
      if (!file || this.buffers.has(file) || this.loading.has(file)) continue;
      if (!this.queue.includes(file)) this.queue.push(file);
    }
    void this.drain();
  }

  /** Files waiting to be fetched quietly, and whether anything is fetching them. */
  private queue: string[] = [];
  private draining = false;

  private async drain(): Promise<void> {
    if (this.draining || !this.context) return;
    this.draining = true;
    try {
      for (let file = this.queue.shift(); file !== undefined; file = this.queue.shift()) {
        await this.load(file);
      }
    } finally {
      this.draining = false;
    }
  }

  private load(file: string): Promise<AudioBuffer | null> {
    const held = this.buffers.get(file);
    if (held) return Promise.resolve(held);
    const pending = this.loading.get(file);
    if (pending) return pending;

    const context = this.context;
    if (!context) return Promise.resolve(null);

    const promise = fetch(urlOf(file))
      .then((response) =>
        response.ok ? response.arrayBuffer() : Promise.reject(new Error('missing')),
      )
      .then((bytes) => context.decodeAudioData(bytes))
      .then((buffer) => {
        this.buffers.set(file, buffer);
        return buffer;
      })
      .catch(() => null)
      .finally(() => this.loading.delete(file));
    this.loading.set(file, promise);
    return promise;
  }

  private play(
    sound: SoundWire,
    channel: SoundChannel,
    at: SoundPlace | null,
    loudness: number,
    anyway = false,
  ): void {
    const context = this.context;
    const output = this.channels[channel];
    if (!context || !output || context.state !== 'running') return;
    if (this.volumes.muted && !anyway) return;

    let gainLevel = sound.volume * loudness;
    let pan = 0;
    if (at) {
      const dx = at.x - this.listener.x;
      const dz = at.z - this.listener.z;
      const away = Math.hypot(dx, dz);
      if (away > HEARD_WITHIN) return;
      gainLevel *= Math.pow(Math.max(0, 1 - away / HEARD_WITHIN), 1.4);
      // Across the screen: the camera's right, from its yaw.
      const rightX = Math.cos(this.listener.yaw);
      const rightZ = -Math.sin(this.listener.yaw);
      pan = Math.max(-0.8, Math.min(0.8, (dx * rightX + dz * rightZ) / 8));
    }
    if (gainLevel <= 0.001) return;

    const asked = performance.now();
    const start = (buffer: AudioBuffer | null): void => {
      if (!buffer || performance.now() - asked > LATE_BY_MS) return;
      const source = context.createBufferSource();
      source.buffer = buffer;
      if (sound.vary > 0) source.playbackRate.value = 1 + (Math.random() * 2 - 1) * sound.vary;
      const gain = context.createGain();
      gain.gain.value = gainLevel;
      source.connect(gain);
      if (pan !== 0 && typeof context.createStereoPanner === 'function') {
        const panner = context.createStereoPanner();
        panner.pan.value = pan;
        gain.connect(panner);
        panner.connect(output);
      } else {
        gain.connect(output);
      }
      source.start();
    };

    const held = this.buffers.get(sound.file);
    if (held) start(held);
    else void this.load(sound.file).then(start);
  }

  private startLoop(cue: string): void {
    const context = this.context;
    const output = this.channels.ambient;
    const held = this.cues.get(cue);
    const name = held?.sounds[0];
    const sound = name ? this.sounds.get(name) : undefined;
    if (!context || !output || !sound || !name) return;

    const gain = context.createGain();
    gain.gain.value = 0;
    gain.connect(output);
    const source = context.createBufferSource();
    source.loop = true;
    source.connect(gain);
    this.loops.set(cue, { source, gain, sound: name });

    void this.load(sound.file).then((buffer) => {
      if (!buffer || this.loops.get(cue)?.source !== source) return;
      source.buffer = buffer;
      source.start();
    });
  }

  private stopLoop(cue: string): void {
    const loop = this.loops.get(cue);
    if (!loop) return;
    this.loops.delete(cue);
    try {
      loop.source.stop();
    } catch {
      // Never started: its file had not arrived.
    }
    loop.gain.disconnect();
  }
}

/** The one the whole client shares. */
export const sounds = new SoundEngine();
